"use client"

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Shield, CheckCircle, Loader2, ExternalLink } from "@/lib/icons"
import { useCloudflareVerificationStore } from '@/lib/cloudflare-verification-store'
import { useSourceStore } from '@/lib/source-store'
import { cn } from '@/lib/utils'
import { useTranslations } from "next-intl"

interface CloudflareVerifierProps {
  sourceId: string
  onVerified?: () => void
  variant?: 'default' | 'inline' | 'compact' | 'icon'
  className?: string
}

export function CloudflareVerifier({ 
  sourceId, 
  onVerified,
  variant = 'default',
  className 
}: CloudflareVerifierProps) {
  const t = useTranslations("cloudflareVerifier")
  const [isVerifying, setIsVerifying] = useState(false)
  const { isVerified, setVerified } = useCloudflareVerificationStore()
  const { getSource } = useSourceStore()
  const source = getSource(sourceId)
  
  const verified = isVerified(sourceId)

  const handleVerify = () => {
    if (!source) return
    
    setIsVerifying(true)
    
    // Get the base URL for the source
    // Prefer homepage if available, otherwise derive from endpoint
    let baseUrl = source.homepage || `https://${sourceId}`
    
    // If no homepage, try to extract from search endpoint
    if (!source.homepage) {
      baseUrl = source.endpoints.search?.split('/index.php')[0] || 
                source.endpoints.search?.split('/post')[0] ||
                source.endpoints.search?.split('/posts')[0] ||
                `https://${sourceId}`
    }
    
    console.log(`[CloudflareVerifier] Opening verification window for ${sourceId}:`, baseUrl)
    
    // Show instructions toast/notification
    if (typeof window !== 'undefined') {
      // Create a simple notification element
      const notification = document.createElement('div')
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #1e293b;
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        max-width: 320px;
        font-size: 14px;
        line-height: 1.5;
      `
      notification.innerHTML = `
        <div style="font-weight: 600; margin-bottom: 8px;">📋 Verification Instructions</div>
        <div style="color: #cbd5e1;">
          1. Wait for the site to load<br/>
          2. Solve any Cloudflare challenge if shown<br/>
          3. Close the window when done<br/>
          <br/>
          <span style="color: #94a3b8; font-size: 12px;">The verification will complete automatically</span>
        </div>
      `
      document.body.appendChild(notification)
      
      // Remove after 8 seconds
      setTimeout(() => {
        notification.remove()
      }, 8000)
    }
    
    // Open site in new window for user to solve captcha
    const verifyWindow = window.open(
      baseUrl,
      `cloudflare-verify-${sourceId}`,
      'width=800,height=600,menubar=no,toolbar=no,location=yes'
    )

    if (!verifyWindow) {
      console.error('[CloudflareVerifier] Failed to open verification window - popup blocked?')
      setIsVerifying(false)
      return
    }

    // Check if verification succeeded
    const checkInterval = setInterval(() => {
      try {
        if (verifyWindow.closed) {
          clearInterval(checkInterval)
          setIsVerifying(false)
          
          // Mark as verified
          setVerified(sourceId)
          
          console.log(`[CloudflareVerifier] ${sourceId} verification complete`)
          
          // Show success notification
          if (typeof window !== 'undefined') {
            const successNotif = document.createElement('div')
            successNotif.style.cssText = `
              position: fixed;
              top: 20px;
              right: 20px;
              background: #10b981;
              color: white;
              padding: 12px 20px;
              border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.3);
              z-index: 10000;
              font-size: 14px;
              font-weight: 500;
            `
            successNotif.textContent = `✓ ${source?.name || sourceId} verified successfully!`
            document.body.appendChild(successNotif)
            
            setTimeout(() => {
              successNotif.remove()
            }, 3000)
          }
          
          // Callback
          onVerified?.()
        }
      } catch (e) {
        // Expected - cross-origin access blocked
      }
    }, 500)

    // Timeout after 5 minutes
    setTimeout(() => {
      if (!verifyWindow.closed) {
        clearInterval(checkInterval)
        verifyWindow.close()
        setIsVerifying(false)
        console.log(`[CloudflareVerifier] ${sourceId} verification timed out`)
      }
    }, 5 * 60 * 1000)
  }

  // Icon-only — for dense rows (the source picker) where a full "Verify" pill per
  // source dominated the row and pushed the source name into truncation. The label
  // lives in the surrounding tooltip instead.
  if (variant === 'icon') {
    return (
      <Button
        onClick={handleVerify}
        disabled={verified || isVerifying}
        size="icon"
        variant="ghost"
        aria-label={verified ? t("cloudflareVerified") : t("verifyCloudflare")}
        className={cn('h-7 w-7', className)}
      >
        {isVerifying ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : verified ? (
          <CheckCircle className="h-3.5 w-3.5" />
        ) : (
          <Shield className="h-3.5 w-3.5" />
        )}
      </Button>
    )
  }

  if (variant === 'compact') {
    return (
      <Button
        onClick={handleVerify}
        disabled={verified || isVerifying}
        size="sm"
        variant={verified ? 'outline' : 'default'}
        className={cn('h-7 text-xs', className)}
      >
        {isVerifying ? (
          <>
            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
            {t("verifying")}
          </>
        ) : verified ? (
          <>
            <CheckCircle className="mr-1.5 h-3 w-3" />
            {t("verified")}
          </>
        ) : (
          <>
            <Shield className="mr-1.5 h-3 w-3" />
            {t("verify")}
          </>
        )}
      </Button>
    )
  }

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <span className="text-sm text-muted-foreground">
          {source?.name || sourceId}
        </span>
        <Button
          onClick={handleVerify}
          disabled={verified || isVerifying}
          size="sm"
          variant={verified ? 'outline' : 'default'}
          className="h-8"
        >
          {isVerifying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("verifying")}
            </>
          ) : verified ? (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              {t("verified")}
            </>
          ) : (
            <>
              <Shield className="mr-2 h-4 w-4" />
              {t("verifyNow")}
            </>
          )}
        </Button>
      </div>
    )
  }

  // Default variant
  return (
    <Button
      onClick={handleVerify}
      disabled={verified || isVerifying}
      variant={verified ? 'outline' : 'default'}
      className={className}
    >
      {isVerifying ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t("verifyingName", { name: source?.name || sourceId })}
        </>
      ) : verified ? (
        <>
          <CheckCircle className="mr-2 h-4 w-4" />
          {t("nameVerified", { name: source?.name || sourceId })}
        </>
      ) : (
        <>
          <Shield className="mr-2 h-4 w-4" />
          {t("verifyName", { name: source?.name || sourceId })}
          <ExternalLink className="ml-2 h-3 w-3" />
        </>
      )}
    </Button>
  )
}

interface CloudflareVerificationPromptProps {
  blockedSources: string[]
  onVerified?: () => void
  className?: string
}

export function CloudflareVerificationPrompt({ 
  blockedSources, 
  onVerified,
  className 
}: CloudflareVerificationPromptProps) {
  const t = useTranslations("cloudflareVerifier")
  if (blockedSources.length === 0) return null

  return (
    <div className={cn(
      'rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 space-y-3',
      className
    )}>
      <div className="flex items-start gap-3">
        <Shield className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <h3 className="font-semibold text-sm">{t("cloudflareVerificationRequired")}</h3>
          <p className="text-xs text-muted-foreground">
            {t("someSourcesMayBeBlockedByCloudflareProtec")}
          </p>
        </div>
      </div>
      
      <div className="space-y-2 pl-8">
        {blockedSources.map((sourceId) => (
          <CloudflareVerifier
            key={sourceId}
            sourceId={sourceId}
            variant="inline"
            onVerified={onVerified}
          />
        ))}
      </div>
    </div>
  )
}
